import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { navigateToTeamMemberFromBreadcrumb, useAthleteStaffForAdminBreadcrumb } from "./AthleteAdminBreadcrumbNav";
import VideoLightbox from "./VideoLightbox";
import { standaloneVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import { isAthlete } from "../utils/auth";
import { buildApiUrl } from "../utils/apiConfig";
import { decodeHtmlEntities } from "../utils/htmlUtils";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/discover-u.css";

interface DiscoverUContent {
    id: number;
    slug: string;
    title: string;
    subtitle: string;
    imageUrl: string;
    videoImageUrl: string;
    videoUrl: string;
    isCurrentSpotlight: boolean;
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function DiscoverUPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentSpotlight, setCurrentSpotlight] = useState<DiscoverUContent | null>(null);
    const [pastSpotlights, setPastSpotlights] = useState<DiscoverUContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ id: number; title: string; vimeoUrl: string } | null>(null);
    const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
    const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && athleteId;

    // Get team member info from navigation state
    const navState = location.state as { teamMemberId?: string; teamMemberName?: string } | null;
    const teamMemberId = navState?.teamMemberId;
    const teamMemberName = navState?.teamMemberName;

    const { staffId: athleteStaffId } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    // Handler for breadcrumb navigation to team member view
    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                if (teamMemberName) {
                    const nameParts = teamMemberName.split(' ');
                    setCoachData({
                        id: teamMemberId,
                        firstName: nameParts[0] || teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                // Fetch Discover U content from the API
                const response = await fetch(
                    buildApiUrl(`/api/discoveru/content`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    }
                );

                if (!response.ok) {
                    throw new Error(`Failed to fetch Discover U content: ${response.status}`);
                }

                const data = await response.json();

                // Separate current spotlight from past spotlights
                const current = data.find((item: DiscoverUContent) => item.isCurrentSpotlight);
                const past = data.filter((item: DiscoverUContent) => !item.isCurrentSpotlight);

                setCurrentSpotlight(current || null);
                setPastSpotlights(past);

                // Initialize loading states for all images
                const initialLoadingStates: Record<number, boolean> = {};
                data.forEach((item: DiscoverUContent) => {
                    initialLoadingStates[item.id] = false;
                });
                setImageLoadingStates(initialLoadingStates);
            } catch (error) {
                console.error("Failed to fetch Discover U data:", error);
                setError("Failed to load Discover U content");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [athleteId, teamMemberId, teamMemberName]);

    // Handler for video thumbnail clicks
    const handleVideoClick = (item: DiscoverUContent) => {
        if (!item.videoUrl) return;

        setSelectedVideo({
            id: item.id,
            title: item.title,
            vimeoUrl: item.videoUrl
        });
    };

    // Handler for image load
    const handleImageLoad = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
    };

    // Handler for image error
    const handleImageError = (itemId: number) => {
        setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
        setImageErrorStates(prev => ({ ...prev, [itemId]: true }));
    };

    if (loading) {
        return (
            <div className={`discover-u-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                <div className="discover-u-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`discover-u-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                <div className="discover-u-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`discover-u-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
            {/* Main Heading - Above everything */}
            {/* Breadcrumb Navigation - Below title when admin is viewing */}
            {isAdminViewingAthlete && (
                <div className="discover-u-breadcrumb-nav">
                    <button
                        onClick={handleNavigateToTeamMember}
                        className="discover-u-breadcrumb-link"
                        data-testid="breadcrumb-back-button"
                    >
                        <svg
                            className="discover-u-breadcrumb-icon"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        <span>
                            Back to {coachData?.firstName} {coachData?.lastName}'s Profile
                        </span>
                    </button>
                </div>
            )}

            {/* Main Content */}
            <div className={`discover-u-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                {/* Current Spotlight Section */}
                {currentSpotlight && (
                    <section className="discover-u-spotlight-section">
                        <div className="discover-u-spotlight-card">
                            {/* Content Info - Left Side */}
                            <div className="discover-u-spotlight-content">
                                <div className="discover-u-spotlight-logo">
                                    {imageLoadingStates[currentSpotlight.id + 1000] && !imageErrorStates[currentSpotlight.id + 1000] && (
                                        <div className="discover-u-logo-loading">
                                            <div className="discover-u-logo-spinner"></div>
                                        </div>
                                    )}
                                    <img
                                        src={imageErrorStates[currentSpotlight.id + 1000] ? "/api/placeholder/200/200" : currentSpotlight.imageUrl}
                                        alt={`${currentSpotlight.title} logo`}
                                            className="discover-u-logo-image"
                                            onLoad={() => handleImageLoad(currentSpotlight.id + 1000)}
                                            onError={() => handleImageError(currentSpotlight.id + 1000)}
                                        />
                                    </div>
                                    <h2 className="discover-u-spotlight-title">
                                        {decodeHtmlEntities(currentSpotlight.title)}
                                    </h2>
                                    <p className="discover-u-spotlight-subtitle">
                                        {decodeHtmlEntities(currentSpotlight.subtitle)}
                                    </p>
                                </div>

                                {/* Video Thumbnail with Play Button - Right Side */}
                                <div
                                    className="discover-u-spotlight-video"
                                    onClick={() => handleVideoClick(currentSpotlight)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleVideoClick(currentSpotlight);
                                        }
                                    }}
                                    data-testid="current-spotlight-video"
                                >
                                    {imageLoadingStates[currentSpotlight.id] && !imageErrorStates[currentSpotlight.id] && (
                                        <div className="discover-u-image-loading">
                                            <div className="discover-u-image-spinner"></div>
                                        </div>
                                    )}
                                    <img
                                        src={imageErrorStates[currentSpotlight.id] ? "/api/placeholder/800/450" : currentSpotlight.videoImageUrl}
                                        alt={currentSpotlight.title}
                                        className="discover-u-spotlight-image"
                                        onLoad={() => handleImageLoad(currentSpotlight.id)}
                                        onError={() => handleImageError(currentSpotlight.id)}
                                    />
                                    <div className="discover-u-play-button">
                                        <svg
                                            className="discover-u-play-icon"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                        >
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Past Spotlights Section */}
                    {pastSpotlights.length > 0 && (
                        <section className="discover-u-past-section">
                            <h2 className="discover-u-section-title">Past Spotlights</h2>
                            <div className="discover-u-grid">
                                {pastSpotlights.map((item) => (
                                    <div
                                        key={item.id}
                                        className="discover-u-card"
                                        data-testid={`past-spotlight-${item.slug}`}
                                    >
                                        {/* Video Thumbnail with Play Button */}
                                        <div
                                            className="discover-u-card-video"
                                            onClick={() => handleVideoClick(item)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleVideoClick(item);
                                                }
                                            }}
                                            data-testid={`past-spotlight-video-${item.id}`}
                                        >
                                            {imageLoadingStates[item.id] && !imageErrorStates[item.id] && (
                                                <div className="discover-u-image-loading">
                                                    <div className="discover-u-image-spinner"></div>
                                                </div>
                                            )}
                                            <img
                                                src={imageErrorStates[item.id] ? "/api/placeholder/400/225" : item.videoImageUrl}
                                                alt={item.title}
                                                className="discover-u-card-image"
                                                onLoad={() => handleImageLoad(item.id)}
                                                onError={() => handleImageError(item.id)}
                                            />
                                            <div className="discover-u-play-button discover-u-play-button-small">
                                                <svg
                                                    className="discover-u-play-icon"
                                                    viewBox="0 0 24 24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Card Content */}
                                        <div className="discover-u-card-content">
                                            <div className="discover-u-card-logo">
                                                {imageLoadingStates[item.id + 1000] && !imageErrorStates[item.id + 1000] && (
                                                    <div className="discover-u-logo-loading-small">
                                                        <div className="discover-u-logo-spinner-small"></div>
                                                    </div>
                                                )}
                                                <img
                                                    src={imageErrorStates[item.id + 1000] ? "/api/placeholder/60/60" : item.imageUrl}
                                                    alt={`${item.title} logo`}
                                                    className="discover-u-card-logo-image"
                                                    onLoad={() => handleImageLoad(item.id + 1000)}
                                                    onError={() => handleImageError(item.id + 1000)}
                                                />
                                            </div>
                                            <h3 className="discover-u-card-title">
                                                {decodeHtmlEntities(item.title)}
                                            </h3>
                                            <p className="discover-u-card-subtitle">
                                                {decodeHtmlEntities(item.subtitle)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Empty State */}
                    {!currentSpotlight && pastSpotlights.length === 0 && (
                        <div className="discover-u-empty">
                            <svg
                                className="discover-u-empty-icon"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                            </svg>
                            <p className="discover-u-empty-text">No content available at this time.</p>
                        </div>
                    )}
            </div>

            {/* Video Lightbox */}
            <VideoLightbox
                open={!!selectedVideo}
                resource={selectedVideo ?? undefined}
                onClose={() => setSelectedVideo(null)}
                analytics={selectedVideo ? standaloneVideoAnalytics(WORDPRESS_TAB.DISCOVER_U, selectedVideo.id) : null}
            />
        </div>
    );
}

